import debounce from "debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../components/loader/Loader";
import OneTournament from "../../../../components/oneTournament/OneTournament";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import {
  fetchTournamentsByQuery,
  resetSearchResults,
} from "../../../../features/tournamentSearch/tournamentSearchSlice";
import { fetchPaginatedTournament } from "../../../../features/tournaments/tournamentsSlice";
import styles from "./allTournaments.module.css";

const SEARCH_DEBOUNCE_MS = 450;

// All tournaments + search on one page (same shape as All Squads). An empty box
// shows the full, infinitely scrolling list; typing swaps it for the search
// matches, so scrolling only loads more while the box is empty.
const AllTournaments = () => {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    list: tournaments,
    fetchStatus,
    currentPage,
    totalPages,
  } = useSelector((state) => state.tournaments);
  const { results: searchResults, status: searchStatus } = useSelector(
    (state) => state.tournamentSearch,
  );

  const [searchValue, setSearchValue] = useState("");
  const searchTerm = searchValue.trim();
  const isSearching = Boolean(searchTerm);

  const tournamentListRef = useRef();
  const debouncedSearchRef = useRef(null);

  const handleRefreshTournaments = useCallback(async () => {
    setIsRefreshing(true);
    tournamentListRef.current?.scrollTo?.({
      top: 0,
      behavior: "smooth",
    });

    try {
      // While searching, refresh re-runs the search instead of the list.
      if (isSearching) {
        await dispatch(fetchTournamentsByQuery(searchTerm)).unwrap();
      } else {
        await dispatch(fetchPaginatedTournament({ page: 1 })).unwrap();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, isSearching, searchTerm]);

  const shownTournaments = isSearching ? searchResults || [] : tournaments;
  const isLoading = isSearching
    ? searchStatus === "loading"
    : fetchStatus === "loading";

  const displayTournaments = shownTournaments.map((item) => {
    return (
      <OneTournament
        tournamentId={item._id}
        key={item._id}
        status={item.status}
        name={item.name}
        serialNumber={item.serialNumber}
        createdBy={item.createdBy}
        startDate={item.startDate}
        endDate={item.endDate}
        description={item.description}
        enDescription={item.enDescription}
        prize={item.prize}
        tournamentImage={item.tournamentImage}
        prizeDescription={item.prizeDescription}
        enPrizeDescription={item.enPrizeDescription}
        playersCount={item.playersCount}
        requiredAds={item.requiredAds}
        requiredAdsByRegion={item.requiredAdsByRegion}
        stage={item.stage}
        liveLink={item.liveLink}
        gameName={item.gameName}
        tournamentType={item.tournamentType}
        maxUsersByTeam={item.maxUsersByTeam}
        detailsTemplate={item.detailsTemplate}
      />
    );
  });

  const handleScroll = useCallback(() => {
    const container = tournamentListRef.current;
    if (!container || fetchStatus === "loading" || isSearching) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentPage < totalPages
    ) {
      dispatch(fetchPaginatedTournament({ page: currentPage + 1 }));
    }
  }, [dispatch, fetchStatus, currentPage, totalPages, isSearching]);

  useEffect(() => {
    if (fetchStatus === "idle") {
      dispatch(fetchPaginatedTournament({ page: 1 }));
    }
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    const container = tournamentListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Always a fresh search (like All Squads): the search cache isn't updated
  // by edits, so reading it could show a tournament's old details.
  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => {
      if (!value) {
        dispatch(resetSearchResults());
        return;
      }
      dispatch(fetchTournamentsByQuery(value));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      debouncedSearchRef.current?.clear?.();
    };
  }, [dispatch]);

  // Leaving the page drops the search so the next visit starts on the list.
  useEffect(
    () => () => {
      dispatch(resetSearchResults());
    },
    [dispatch],
  );

  const handleSearch = useCallback((event) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearchRef.current?.(value.trim());
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.headerTitleGroup}>
          <TextWrapper textStyle="textTitle">جميع البطولات</TextWrapper>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => {
              Promise.resolve(handleRefreshTournaments()).catch(() => {});
            }}
            disabled={isLoading || isRefreshing}
            title="تحديث جميع البطولات"
            aria-label="تحديث جميع البطولات"
          >
            {isRefreshing ? (
              <Loader size={18} color="var(--color-accent-primary)" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} />
            )}
          </button>
        </div>
      </div>
      <div className={styles.allAdminsWrapper}>
        <DisplayContainer ref={tournamentListRef}>
          <div className={styles.searchInputInnerWrapper}>
            <div className={styles.searchInputShell}>
              <TextInput
                height="50px"
                width="100%"
                borderRadius="12px"
                placeHolder="ابحث عن بطولة"
                bgColor={"grey"}
                value={searchValue}
                onChange={handleSearch}
                bordered
              />
            </div>
          </div>

          {isSearching && searchStatus === "loading" ? null : displayTournaments}

          {isLoading && (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}

          {!isLoading && isSearching && shownTournaments.length === 0 && (
            <div className={styles.emptyState}>
              <TextWrapper textStyle="textMid">لا توجد بطولات مطابقة</TextWrapper>
            </div>
          )}
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllTournaments;
